import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';

import {
  EMPLOYEES_BULK_CREATE_FILE_DATA_STARTING_LINE,
  EMPLOYEES_BULK_CREATE_FILE_VERSION,
  EMPLOYEES_MULTI_COMPANY_BULK_CREATE_FILE_VERSION,
} from '@Common/constants/b2b/employees';

import { downloadBuffer } from '@sh4res/bo-common/utils/downloadBuffer';

const COLUMNS_TO_REMOVE = ['IBAN', 'BIC'];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- internal xlsx-populate structure
const removeQuotePrefix = (workbook: any) => {
  const xfs = workbook._styleSheet?._cellXfsNode?.children;
  if (Array.isArray(xfs)) {
    xfs.forEach((xf) => {
      if (xf?.attributes?.quotePrefix) {
        delete xf.attributes.quotePrefix;
      }
    });
  }
};

const prepareWorkbookSheet = async (file: string) => {
  const response = await fetch(file);
  const fileBuffer = await response.arrayBuffer();
  const workbook = await XlsxPopulate.fromDataAsync(fileBuffer);
  removeQuotePrefix(workbook);
  const sheet = workbook.sheet('import');
  return { workbook, sheet };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- it's typed as any from the lib
const clearDataRows = (sheet: any) => {
  const lastRow = sheet.usedRange().endCell().rowNumber();
  const lastColumnLetter = sheet.usedRange().endCell().columnName();
  const range = sheet.range(
    `A${EMPLOYEES_BULK_CREATE_FILE_DATA_STARTING_LINE}:${lastColumnLetter}${lastRow}`,
  );
  range.clear();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- it's typed as any from the lib
const hideCspColumnIfNeeded = (sheet: any, shouldHideCSPCode: boolean) => {
  if (!shouldHideCSPCode) {
    return;
  }

  const lastColumn = sheet.usedRange().endCell().columnNumber();
  let cspColumn = 1;
  while (sheet.cell(2, cspColumn).value() !== 'CSP_code' && cspColumn <= lastColumn) {
    cspColumn++;
  }

  if (cspColumn <= lastColumn) {
    sheet.column(cspColumn).hidden(true);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- it's typed as any from the lib
const appendCompanyIdColumn = (sheet: any, removedColumnsCount: number) => {
  const trueLastColumn = sheet.usedRange().endCell().columnNumber() - removedColumnsCount;
  sheet.cell(2, trueLastColumn + 1).value('Company_ID*');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- it's typed as any from the lib
const bumpGuideVersion = (workbook: any, version: string) => {
  const guideSheet = workbook.sheet("Guide d'utilisation_FR");
  if (guideSheet) {
    guideSheet.cell(3, 5).value(`Version ${version}`);
  }
};

export const downloadBulkUploadFile = async ({
  file,
  exportFileName,
  shouldHideCSPCode,
}: {
  file: string;
  exportFileName: string;
  shouldHideCSPCode: boolean;
}) => {
  const { workbook, sheet } = await prepareWorkbookSheet(file);

  clearDataRows(sheet);
  COLUMNS_TO_REMOVE.forEach((column) => removeColumn(sheet, column));
  hideCspColumnIfNeeded(sheet, shouldHideCSPCode);
  bumpGuideVersion(workbook, EMPLOYEES_BULK_CREATE_FILE_VERSION);

  const buffer = await workbook.outputAsync();

  downloadBuffer(buffer, `${exportFileName}.xlsm`);
};

export const downloadMultiCompanyBulkUploadFile = async ({
  file,
  exportFileName,
  shouldHideCSPCode,
}: {
  file: string;
  exportFileName: string;
  shouldHideCSPCode: boolean;
}) => {
  const { workbook, sheet } = await prepareWorkbookSheet(file);

  clearDataRows(sheet);
  COLUMNS_TO_REMOVE.forEach((column) => removeColumn(sheet, column));
  hideCspColumnIfNeeded(sheet, shouldHideCSPCode);
  appendCompanyIdColumn(sheet, COLUMNS_TO_REMOVE.length);
  bumpGuideVersion(workbook, EMPLOYEES_MULTI_COMPANY_BULK_CREATE_FILE_VERSION);

  const buffer = await workbook.outputAsync();

  downloadBuffer(buffer, `${exportFileName}.xlsm`);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- it's typed as any from the lib
const removeColumn = (sheet: any, columnTitle: string) => {
  const lastColumn = sheet.usedRange().endCell().columnNumber();
  let columnToRemove = 1;
  while (sheet.cell(2, columnToRemove).value() !== columnTitle && columnToRemove <= lastColumn) {
    columnToRemove++;
  }

  if (columnToRemove <= lastColumn) {
    const usedRange = sheet.usedRange();
    const lastRowNumber = usedRange.endCell().rowNumber();

    for (let rowNumber = 1; rowNumber <= lastRowNumber; rowNumber++) {
      const row = sheet.row(rowNumber);

      // Move cells to the left from next column to the last column
      for (let col = columnToRemove; col < lastColumn; col++) {
        const currentCell = row.cell(col);
        const nextCell = row.cell(col + 1);

        if (nextCell.value() !== null) {
          currentCell.value(nextCell.value());
        } else {
          currentCell.value(null);
        }
      }

      const lastCell = row.cell(lastColumn);
      lastCell.value(null);
    }
  }
};
